import { z } from 'zod';
import { RawDescriptionOutput, MetaTags } from './seo.js';

// ─── AiPromptKind schema ────────────────────────────────────────────────────────

export const aiPromptKindSchema = z.enum([
  'describe',
  'categorize',
  'seo_variant',
  'interior',
  'interior_creative',
  'translate_categories',
  'generate_categories',
  'cascade_categories',
  'translate_wallpapers',
  'generate_wallpapers',
  'translate_blog',
]);

// ─── Step1Describe output schema ─────────────────────────────────────────────

/**
 * step1DescribeOutputSchema — zod version of Step1DescribeOutput.
 * Shape: wallpaper_id, image_url, AI vision analysis fields, generated_at.
 * Re-uses RawDescriptionOutput from seo.ts (source of truth).
 */
export const step1DescribeOutputSchema = RawDescriptionOutput;
export type Step1DescribeOutputSchema = z.infer<typeof step1DescribeOutputSchema>;
/** Alias for Step1DescribeOutputSchema — matches legacy SeoProvider type name. */
export type Step1DescribeOutput = Step1DescribeOutputSchema;

// ─── Step2Seo output schema ───────────────────────────────────────────────────

/**
 * step2SeoOutputSchema — zod version of Step2SeoOutput.
 * Shape: MetaTags (title, description, h1, uri, seo_text) + needsReview flag.
 * Re-uses MetaTags from seo.ts (source of truth).
 */
export const step2SeoOutputSchema = MetaTags.extend({
  needsReview: z.boolean(),
});
export type Step2SeoOutputSchema = z.infer<typeof step2SeoOutputSchema>;
/** Alias for Step2SeoOutputSchema — matches legacy SeoProvider type name. */
export type Step2SeoOutput = Step2SeoOutputSchema;

// ─── Step3Categorize (a.k.a. categorize) ─────────────────────────────────────

/**
 * Wire shape — what the categorizer LLM is asked to return. Per-assignment
 * `reasoning` is optional and is surfaced as a tooltip in the admin UI. No
 * top-level reasoning, no `is_primary` (the prompt no longer asks for it).
 */
const step3AiAssignmentSchema = z.object({
  id: z.number().int().positive(),
  confidence: z.number().min(0).max(1),
  reasoning: z.string().optional(),
});

export const step3CategorizeAiResponseSchema = z.object({
  assignments: z
    .array(step3AiAssignmentSchema)
    .min(1, 'at least one assignment required'),
});

export type Step3CategorizeAiResponse = z.infer<typeof step3CategorizeAiResponseSchema>;

/**
 * Storage shape — what we persist into `wallpaper_ai_steps.output` and what
 * the admin FE reads. We keep `category_id` (rather than `id`) and a frozen
 * `is_primary: false` for compatibility with downstream code and DB columns;
 * the field is no longer meaningful and may be retired later.
 */
const step3StoredAssignmentSchema = z.object({
  category_id: z.number().int().positive(),
  confidence: z.number().min(0).max(1),
  is_primary: z.boolean(),
  reasoning: z.string().optional(),
});

export const step3CategorizeOutputSchema = z.object({
  assignments: z
    .array(step3StoredAssignmentSchema)
    .min(1, 'at least one assignment required'),
});

export type Step3CategorizeOutput = z.infer<typeof step3CategorizeOutputSchema>;
